import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import GoBackButton from '@/common/components/GoBackButton'
import TextField from '@/common/components/TextField'
import { ChevronsRight } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import useBankInstitution from '../../hooks/useBankInstitution'
import { SubmitHandler, useForm } from 'react-hook-form'
import { UpdateBankInstitutionValidationSchema, updateSchema } from '../../schemas/create.schema'
import { useUpdateBankInstitution } from '../../hooks/useUpdateBankInstitution'
import { useGoBack } from '@/common/hooks/useGoBack'
import { useQueryClient } from '@tanstack/react-query'
import { FormTransformers } from '@/common/utils/formTransformers'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import useUserPermissions from '@/common/hooks/useUserPermissions'

const Update = () => {
  const { id } = useParams();
  const queryClient = useQueryClient()
  const goBack = useGoBack()

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const {
    data: initialBankInstitution,
    isPending: initialBankInstitutionIsPending,
    error: initialBankInstitutionError = {} as any,
    refetch: refetchBankInstitution,
  } = useBankInstitution(id || '');

  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm<UpdateBankInstitutionValidationSchema>({
    resolver: zodResolver(updateSchema),
    defaultValues: {
      code: initialBankInstitution?.code,
      name: initialBankInstitution?.name,
      account_validation_regex: initialBankInstitution?.account_validation_regex,
      branch_validation_regex: initialBankInstitution?.branch_validation_regex,
    },
  })

    const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const hasUpdateClientRolePermission = !isUserPermissionsPending ? hasUserPermission('Admin::BankInstitutions::Update') : true

  const { mutate: updateBankInstitution, isPending: updateBankInstitutionIsPending } = useUpdateBankInstitution({
    bankInstitutionId: id || '',
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bankInstitution', id] })
      goBack({ fallback: `/bank-institutions/${id}` })
    },
    onError: FormTransformers.errorsTransformer({ setError, setMessages }),
  })

  const onSubmit: SubmitHandler<UpdateBankInstitutionValidationSchema> = (data) => updateBankInstitution(data)

  useEffect(() => {
    if (initialBankInstitutionIsPending) return

    reset({
      code: initialBankInstitution?.code,
      name: initialBankInstitution?.name,
      account_validation_regex: initialBankInstitution?.account_validation_regex,
      branch_validation_regex: initialBankInstitution?.branch_validation_regex,
    })
  }, [initialBankInstitutionIsPending])

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-128px)]"
      statusCode={hasUpdateClientRolePermission ? 200 : 403}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-2 items-center">
            <GoBackButton fallback={`/bank-institutions/${id}`} />

            <p className="text-white-400 font-normal">
              Instituições bancárias
            </p>

            <ChevronsRight color="#ffffffb3" />

            <h2>{initialBankInstitution?.name}</h2>
          </div>

          <div className="flex gap-4">
            <Link to={`/bank-institutions/${id}`}>
              <Button variant="outline-error">Cancelar</Button>
            </Link>

            <Button
              data-testid="update_bank_institution_button"
              type="submit"
              className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
              isLoading={updateBankInstitutionIsPending}
            >
              Confirmar
            </Button>
          </div>
        </div>

        {initialBankInstitutionIsPending ? (
          <Box>Carregando...</Box>
        ) : (
          <ErrorMessageWrapper
            className="max-h-[calc(100vh-128px)]"
            statusCode={initialBankInstitutionError?.status}
            refetchFunction={refetchBankInstitution}
          >
            <Box className="flex max-h-[calc(100vh-128px)] flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] overflow-y-auto">
              <p className="text-lg font-bold">Novo instituição bancária</p>

              <div className="flex gap-2">
                <TextField
                  label="Código"
                  className="w-1/2"
                  markAsRequired
                  defaultValue={String(initialBankInstitution?.code)}
                  errorMessage={errors?.code?.message || undefined}
                  {...register("code")}
                  disabled={updateBankInstitutionIsPending}
                />
                <TextField
                  label="Nome"
                  className="w-1/2"
                  markAsRequired
                  defaultValue={initialBankInstitution?.name}
                  errorMessage={errors?.name?.message || undefined}
                  {...register("name")}
                  disabled={updateBankInstitutionIsPending}
                />
              </div>
              <div className="flex gap-2">
                <TextField
                  label="Regex de validação de conta"
                  className="w-1/2"
                  markAsRequired
                  defaultValue={initialBankInstitution?.account_validation_regex}
                  errorMessage={errors?.account_validation_regex?.message || undefined}
                  {...register("account_validation_regex")}
                  disabled={updateBankInstitutionIsPending}
                />
                <TextField
                  label="Regex de validação de filial"
                  className="w-1/2"
                  markAsRequired
                  defaultValue={initialBankInstitution?.branch_validation_regex}
                  errorMessage={errors?.branch_validation_regex?.message || undefined}
                  {...register("branch_validation_regex")}
                  disabled={updateBankInstitutionIsPending}
                />
              </div>
            </Box>
          </ErrorMessageWrapper>
        )}

        <ModalMessageSimple messages={messages} setMessages={setMessages} isError />
      </form>
    </ErrorMessageWrapper>
  )
}

export default Update
